import React, { useState, useEffect, useMemo, useRef, useContext } from 'react';

import {
  getCurrentShift,
  getCurrentOrder,
  healthCheck,
} from '../../services/pos/pos';

import AppLoader from '../auth/AppLoader';
import AuthContext from '../auth';
import { upatePosStatus } from '../../services/pos/taxonomies-pos';

const POSContext = React.createContext(null);

export const POSContextWrapper = ({ children }) => {
  const [loaded, setLoaded] = useState(false);
  const { user } = useContext(AuthContext);

  const [shift, setShift] = useState(null);
  const [order, setOrder] = useState(null);
  const [cardBlocked, setCardBlocked] = useState(false);
  const [attachmentForm, setAttachmentForm] = useState(null);
  const [posName, setPosName] = useState('');

  const [posStatusAlertTime] = useState(window._env_.POS_STATS_ALERT_TiME);
  const isPosStatusAlertTime = posStatusAlertTime !== '0';
  const refreshTimeSysParams = posStatusAlertTime;

  const refTime = useRef(Date.now());
  const [alerTrigger, setAlerTrigger] = useState(0);

  const refreshRate = useMemo(() => {
    if (!isPosStatusAlertTime) return null;
    if (refreshTimeSysParams !== null && refreshTimeSysParams !== undefined) {
      const refreshTime = refreshTimeSysParams;
      return refreshTime !== null ? refreshTime * 1000 : null;
    }
    return null;
  }, [refreshTimeSysParams, isPosStatusAlertTime]);

  useEffect(() => {
    if (!refreshRate) return undefined;
    const intervalId = setInterval(
      () => setAlerTrigger((val) => val + 1),
      refreshRate + 1,
    );
    return () => clearInterval(intervalId);
  }, [refreshRate]);

  useEffect(() => {
    const checkHealth = async () => {
      if (!refreshRate) return;

      if (Date.now() - refTime.current > refreshRate) {
        refTime.current = Date.now();
        let posStatus = {
          posId: user?.posId,
          userId: user?.id,
          shiftId: shift ? shift.id : null,
          shiftOpened: shift !== null,
          posLastConnectionAt: new Date(),
          devconLastConnectionAt: null, // Default to null
          workstation: null, // Default to null
          devconWorking: false,
        };
        try {
          const result = await healthCheck(); // Wait for healthCheck to resolve
          posStatus = {
            ...posStatus,
            workstation: result.workstation,
            devconLastConnectionAt: new Date(),
            devconWorking: true,
          };
        } catch (err) {
          console.error('Health check failed:', err);
        }
        upatePosStatus(posStatus); // Call updatePosStatus with the posStatus object
      }
    };

    checkHealth(); // Invoke the async function
  }, [alerTrigger, refreshRate, shift, user]);

  useEffect(() => {
    setLoaded(false);

    getCurrentShift()
      .then((currentShift) => {
        setShift(currentShift);
        return getCurrentOrder(currentShift).then((currentOrder) => {
          if (currentOrder.card.deleted) {
            setCardBlocked(currentOrder.card.cardPhysicalId);
          }
          setOrder(currentOrder);
        });
      })
      .catch((err) => {
        console.error(err);
      })
      .finally(() => setLoaded(true));
  }, []);

  const contextValue = useMemo(
    () => ({
      shift,
      setShift,
      order,
      setOrder,
      cardBlocked,
      setCardBlocked,
      attachmentForm,
      setAttachmentForm,
      posName,
      setPosName,
    }),
    [shift, order, cardBlocked, attachmentForm, posName],
  );

  return (
    <POSContext.Provider value={contextValue}>
      {loaded ? children : <AppLoader />}
    </POSContext.Provider>
  );
};

export default POSContext;
